import React from 'react';
import SiteNav from '../components/SiteNav';
import Footer from '../components/Footer';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/Reveal';
import SecuritySignal from '../components/SecuritySignal';

const mono: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" };

const microEyebrow: React.CSSProperties = {
  ...mono,
  fontSize: '.6875rem',
  letterSpacing: '.18em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const THREAT_GLOSSARY = [
  {
    term: 'At-rest exposure',
    definition: 'Authority or public-key material is visible before a sensitive action occurs.',
  },
  {
    term: 'On-spend exposure',
    definition: 'A live action creates a short attack window before settlement or finality.',
  },
  {
    term: 'Migration exposure',
    definition: 'Active systems need a path to harden controls without disrupting users.',
  },
  {
    term: 'Live-system response',
    definition: 'Operators need provable halt, recovery, and exception handling when risk appears.',
  },
];

const GOOGLE_THREAT_COVERAGE: { layer: string; risk: string; pattern: string; qssn: string }[] = [
  {
    layer: 'Exposed authority',
    risk: 'Long-lived signing authority becomes more sensitive as public-key assumptions weaken.',
    pattern: 'At-rest exposure',
    qssn: 'Separate post-quantum authorization is required before privileged actions can execute.',
  },
  {
    layer: 'Transaction windows',
    risk: 'High-value actions can reveal material or intent at the moment they enter live rails.',
    pattern: 'On-spend exposure',
    qssn: 'The action, policy, nonce, and approval path are bound before execution.',
  },
  {
    layer: 'Migration drag',
    risk: 'Moving an asset program can be slower and riskier than hardening the control surface around it.',
    pattern: 'Migration exposure',
    qssn: 'Controls are strengthened without chain updates, re-issuance, or forced counterparty migration.',
  },
  {
    layer: 'Assurance gap',
    risk: 'Teams need evidence that sensitive actions were reviewed, authorized, and recoverable.',
    pattern: 'Live-system response',
    qssn: 'Monitoring, halt paths, and recovery workflows create an auditable control record.',
  },
];

const OPERATE = [
  {
    kicker: 'Operate',
    title: 'Controlled signing environments',
    body: 'Signing material lives in governed key-management environments.',
    points: ['Key custody policy', 'Operator role map'],
  },
  {
    kicker: 'Watch',
    title: 'Authorization visibility',
    body: 'Teams watch approvals, exceptions, failed checks, and unusual admin activity.',
    points: ['Alert routing', 'Exception logs'],
  },
  {
    kicker: 'Intervene',
    title: 'Halt and recovery paths',
    body: 'Lost keys, compromised operators, and governance actions need rehearsed runbooks.',
    points: ['Recovery runbook', 'Halt procedure'],
  },
  {
    kicker: 'Prove',
    title: 'Assurance evidence',
    body: 'Audits, penetration testing, and evidence collection are operating work.',
    points: ['Test reports', 'Audit artifacts'],
  },
];

const cellHead: React.CSSProperties = {
  background: 'var(--bg-alt)',
  padding: '.9rem 1.1rem',
  ...mono,
  fontSize: '.6rem',
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const Security: React.FC = () => (
  <>
    <SiteNav />

    <PageHeader
      eyebrow="Security & operations"
      title={
        <>
          <span style={{ display: 'block' }}>Keep every control.</span>
          <span style={{ display: 'block', textAlign: 'right' }}>Prove every action.</span>
        </>
      }
      aside={<SecuritySignal />}
    />

    {/* 01 — Google threat model */}
    <section style={{ background: 'var(--bg-alt)', padding: '7rem 1.5rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--line)',
            paddingTop: '1.25rem',
            marginBottom: '3.5rem',
          }}
        >
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>{'Google threat model'}</span>
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>01</span>
        </Reveal>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3rem 4rem' }}>
          <div style={{ flex: '1 1 340px' }}>
            <Reveal
              as="h2"
              style={{
                margin: 0,
                fontWeight: 500,
                fontSize: 'clamp(2rem,3.4vw,3.1rem)',
                lineHeight: 1.05,
                letterSpacing: '-.02em',
                color: 'var(--ink)',
                textWrap: 'balance',
              } as React.CSSProperties}
            >
              How QSSN mitigates quantum risk at the control layer.
            </Reveal>
            <Reveal as="p" delay={80} style={{ margin: '1.75rem 0 0', maxWidth: '30rem', fontSize: '1.0625rem', lineHeight: 1.6, color: 'var(--ink-2)' }}>
              Google Quantum AI's cryptocurrency security paper quantifies the timeline for quantum attacks against elliptic-curve cryptography. QSSN applies that threat model to the operating layer where institutions authorize high-value digital-asset actions.
            </Reveal>
            <Reveal delay={120} style={{ marginTop: '1.6rem' }}>
              <a
                href="https://arxiv.org/abs/2603.28846"
                target="_blank"
                rel="noreferrer"
                style={{ display: 'inline-flex', fontSize: '.875rem', fontWeight: 500, color: 'var(--accent)', textDecoration: 'none' }}
              >
                View the Google Quantum AI paper
              </a>
            </Reveal>
            <Reveal delay={160} style={{ marginTop: '2rem', borderTop: '1px solid var(--line)', paddingTop: '1.25rem' }}>
              <div style={{ ...mono, fontSize: '.625rem', letterSpacing: '.16em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: 'var(--ink-3)', marginBottom: '1rem' }}>
                Threat pattern glossary
              </div>
              <div style={{ display: 'grid', gap: '.9rem' }}>
                {THREAT_GLOSSARY.map((item) => (
                  <div key={item.term} style={{ display: 'grid', gap: '.25rem' }}>
                    <div style={{ fontSize: '.875rem', fontWeight: 500, color: 'var(--ink)' }}>{item.term}</div>
                    <p style={{ margin: 0, fontSize: '.8125rem', lineHeight: 1.45, color: 'var(--ink-2)' }}>{item.definition}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal className="qssn-threat-table" style={{ flex: '1 1 620px', alignSelf: 'flex-start', border: '1px solid var(--line)', borderRadius: 14, overflow: 'hidden' }}>
            <div className="qssn-threat-table-head" style={{ display: 'grid', gridTemplateColumns: '.95fr 1.25fr .95fr 1.7fr', background: 'var(--line)', gap: 1 }}>
              <div style={{ ...cellHead, color: 'var(--ink-3)' }}>Layer</div>
              <div style={{ ...cellHead, color: 'var(--ink-3)' }}>Risk surfaced</div>
              <div style={{ ...cellHead, color: 'var(--ink-3)' }}>Threat pattern</div>
              <div style={{ ...cellHead, color: 'var(--accent)' }}>QSSN coverage</div>
            </div>
            <div className="qssn-threat-table-body" style={{ display: 'grid', background: 'var(--line)', gap: 1 }}>
              {GOOGLE_THREAT_COVERAGE.map((row, index) => (
                <div key={row.layer} className="qssn-threat-table-row" style={{ display: 'grid', gridTemplateColumns: '.95fr 1.25fr .95fr 1.7fr', gap: 1 }}>
                  <div className="qssn-threat-table-cell" style={{ background: 'var(--surface)', padding: '.95rem 1rem', fontSize: '.9375rem', fontWeight: 500, lineHeight: 1.35, color: 'var(--ink)' }}>
                    <span style={{ display: 'block', ...mono, marginBottom: '.45rem', fontSize: '.6rem', letterSpacing: '.12em', color: 'var(--ink-3)' }}>{String(index + 1).padStart(2, '0')}</span>
                    {row.layer}
                  </div>
                  <div className="qssn-threat-table-cell" style={{ background: 'var(--surface)', padding: '.95rem 1rem', fontSize: '.8125rem', lineHeight: 1.42, color: 'var(--ink-2)' }}>{row.risk}</div>
                  <div className="qssn-threat-table-cell" style={{ background: 'var(--surface)', padding: '.95rem 1rem', fontSize: '.8125rem', lineHeight: 1.42, color: 'var(--ink)' }}>{row.pattern}</div>
                  <div className="qssn-threat-table-cell qssn-threat-table-cell-coverage" style={{ background: 'var(--surface)', padding: '.95rem 1rem', fontSize: '.8125rem', lineHeight: 1.42, color: 'var(--accent)' }}>{row.qssn}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* 02 — Built to operate */}
    <section style={{ background: 'var(--bg)', padding: '7rem 1.5rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--line)',
            paddingTop: '1.25rem',
            marginBottom: '3.5rem',
          }}
        >
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>{'Built to operate'}</span>
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>02</span>
        </Reveal>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3rem 4rem' }}>
          <div style={{ flex: '1 1 340px' }}>
            <Reveal
              as="h2"
              style={{
                margin: 0,
                fontWeight: 500,
                fontSize: 'clamp(2rem,3.4vw,3.1rem)',
                lineHeight: 1.05,
                letterSpacing: '-.02em',
                color: 'var(--ink)',
                textWrap: 'balance',
              } as React.CSSProperties}
            >
              Built for operational assurance — people, keys, alerts, and recovery.
            </Reveal>
            <Reveal as="p" delay={80} style={{ margin: '1.75rem 0 0', maxWidth: '30rem', fontSize: '1.0625rem', lineHeight: 1.6, color: 'var(--ink-2)' }}>
              Assess QSSN as operating infrastructure: key governance, exception monitoring, halt paths, and readiness evidence.
            </Reveal>
          </div>

          <Reveal style={{ flex: '1 1 480px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '1.5rem' }}>
            {OPERATE.map((card) => (
              <article key={card.kicker} style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: '1.5rem' }}>
                <div style={{ ...mono, fontSize: '.625rem', letterSpacing: '.16em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: 'var(--accent)' }}>{card.kicker}</div>
                <h3 style={{ margin: '.85rem 0 0', fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.25, color: 'var(--ink)' }}>{card.title}</h3>
                <p style={{ margin: '.7rem 0 0', fontSize: '.9375rem', lineHeight: 1.5, color: 'var(--ink-2)' }}>{card.body}</p>
                <div style={{ marginTop: '1.1rem', display: 'grid', gap: '.55rem' }}>
                  {card.points.map((pt) => (
                    <div key={pt} style={{ display: 'flex', alignItems: 'flex-start', gap: '.6rem', fontSize: '.8125rem', color: 'var(--ink-2)' }}>
                      <span style={{ marginTop: '.35rem', width: 6, height: 6, background: 'var(--accent)' }} />
                      {pt}
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </Reveal>
        </div>
      </div>
    </section>

    <Footer />
  </>
);

export default Security;
